import { fetchWithChatFeedback } from '../../api/chatFeedbackRequired';
/**
 * Thin fetch-based client for the /api/far-chat/* endpoints.
 *
 * All calls:
 *   - prefix the REACT_APP_API_URL base (or empty for same-origin)
 *   - send credentials (cookies) via `credentials: 'include'`
 *   - throw `FarChatApiError` on non-2xx
 *
 * Mirrors the pattern used by `ui-poc/src/api/wageDetFunctions.ts`.
 */

import {
  Attachment,
  AttachmentsEnvelope,
  Citation,
  ClarificationEnvelope,
  FarChatApiError,
  FastAnswerRecoveryError,
  isFastAnswerRecoveryEnvelope,
  FarRegime,
  ThreadEnvelope,
  TopicClarificationEnvelope,
  Turn,
  TurnRequest,
  TurnResponse,
} from './types';

const API_BASE_URL: string = process.env.REACT_APP_API_URL || '';

function authHeaders(extra: Record<string, string> = {}): Record<string, string> {
  const headers: Record<string, string> = { ...extra };
  return headers;
}

async function assertOk(resp: Response): Promise<void> {
  if (resp.ok) return;
  let body: unknown;
  try {
    body = await resp.json();
  } catch {
    try {
      body = await resp.text();
    } catch {
      body = undefined;
    }
  }
  const message =
    (body && typeof body === 'object' && 'detail' in (body as any)
      ? String((body as any).detail)
      : `HTTP ${resp.status}`) || `HTTP ${resp.status}`;
  throw new FarChatApiError(message, resp.status, body);
}

function wfUrl(workflowId: string, suffix: string = ''): string {
  return `${API_BASE_URL}/api/far-chat/workflows/${encodeURIComponent(workflowId)}${suffix}`;
}

export async function fetchThread(workflowId: string): Promise<ThreadEnvelope> {
  const resp = await fetchWithChatFeedback(wfUrl(workflowId, '/thread'), {
    method: 'GET',
    headers: authHeaders(),
    credentials: 'include',
  });
  await assertOk(resp);
  return (await resp.json()) as ThreadEnvelope;
}

export async function postTurn(
  workflowId: string,
  req: TurnRequest
): Promise<TurnResponse> {
  const resp = await fetchWithChatFeedback(wfUrl(workflowId, '/turn'), {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(req),
    credentials: 'include',
  });
  await assertOk(resp);
  return (await resp.json()) as TurnResponse;
}

/**
 * v2 turn wire-shape (response from POST /v2/turn AND each entry in the
 * ``turns`` array on GET /thread). Kept here so it's the single source of
 * truth for how raw v2 turns arrive from the backend.
 */
export interface RawV2Turn {
  turn_id: string;
  user_msg: string;
  response: {
    summary: string;
    detail: string;
    citations: Array<Citation | string>;
  };
  reader_outputs?: unknown;
  is_refusal?: boolean;
  refusal_text?: string;
  timestamp?: string;
  /** Canonical durable position in a unified Home Chat transcript. */
  timeline_sequence?: number;
  /** Legacy compatibility fallback; new server responses use timeline_sequence. */
  order?: number;
  /**
   * Chosen clarified question (the query that produced `response`) when
   * the clarification gate fired and the user picked a rewrite that
   * differs from their original. `null`/absent otherwise (legacy turns,
   * "use my original", or gate never fired). Rehydrated by
   * :func:`normalizeV2Turn` so the reload path can render the
   * "Clarified: …" callout below the original query.
   */
  clarification?: string | null;
  /**
   * Chosen post-reader topic focus (the topic label or typed free text that
   * shaped `response`) when the topic-focus gate fired and the user picked a
   * focus. `null`/absent otherwise (legacy turns, Skip, gate off, or gate
   * never fired). Rehydrated by :func:`normalizeV2Turn` so the reload path can
   * render the "Focused on: …" callout below the original query.
   */
  topic_focus?: string | null;
  /**
   * Deterministic backend flag: `true` iff the answer cites FAR/RFO. Absent
   * on legacy/persisted turns (and on a normal grounded answer) → treated as
   * `true` (no "not based on FAR/RFO" banner). Only an explicit `false`
   * triggers the banner. Lifted by :func:`normalizeV2Turn`.
   */
  references_far?: boolean;
  training_guidance?: boolean;
  /**
   * Proactive feedback solicitation marker. Set (with a backend-minted
   * prompt id) only on a live answered turn the scheduler chose to ask
   * "How was this response?" about; `null`/absent otherwise — including
   * every history-served turn (the flag is never persisted). Lifted by
   * :func:`normalizeV2Turn` to `Turn.feedbackRequest`. See
   * issues/20260702-chat-feedback-random_spec.md §5.
   */
  feedback_request?: { prompt_id: string } | null;
  /** Additive server authorization for ordinary Home Chat edit/resend only. */
  editable?: boolean;
}

/**
 * Normalize a raw v2 turn dict (the wire shape returned by both
 * ``POST /v2/turn`` and the ``turns`` array on ``GET /thread``) into the
 * frontend ``Turn`` shape. Lifts ``response.{summary,detail,citations}``
 * to top-level fields and runs each citation list through
 * :func:`normalizeCitations`.
 *
 * Reused by ``postV2Turn`` (live turns) and the HomeChat / FarChat reload
 * paths (rehydrating previously-saved sessions) so the two stay in sync.
 */
export function normalizeV2Turn(raw: RawV2Turn): Turn {
  const summary = raw.response?.summary ?? '';
  const detail = raw.response?.detail ?? '';
  const citations = normalizeCitations(raw.response?.citations ?? []);
  return {
    turn_id: raw.turn_id,
    user_msg: raw.user_msg,
    summary,
    detail,
    citations,
    is_refusal: !!raw.is_refusal,
    refusal_text: raw.refusal_text || (raw.is_refusal ? summary : undefined),
    timestamp: raw.timestamp,
    timeline_sequence: raw.timeline_sequence,
    order: raw.order,
    clarification: raw.clarification ?? null,
    topic_focus: raw.topic_focus ?? null,
    // Default true: missing/absent (legacy turns, grounded answers) → true so
    // no banner renders. Only an explicit `false` flips it off.
    references_far: raw.references_far !== false,
    training_guidance: raw.training_guidance === true,
    feedbackRequest: raw.feedback_request?.prompt_id
      ? { promptId: raw.feedback_request.prompt_id }
      : null,
    editable: raw.editable === true,
  };
}

/**
 * v2 turn endpoint. Backend wire shape:
 *   {
 *     turn_id, user_msg,
 *     response: { summary, detail, citations: string[] },
 *     reader_outputs,
 *     is_refusal, timestamp
 *   }
 *
 * The frontend `Turn` interface flattens `response.{summary,detail,citations}`
 * onto the `Turn` object itself; the lifting + citation normalization
 * lives in :func:`normalizeV2Turn` so the reload path can reuse it.
 */
export interface PostV2TurnOptions {
  /**
   * Token from a `topic_clarification_needed` envelope. Its PRESENCE makes
   * the backend skip Stage-1 AND the readers, load the held reader outputs,
   * and run the synth on the chosen focus. Forwarded as `focus_token`.
   */
  focusToken?: string;
  /**
   * The chosen post-reader focus: a topic label, typed free text, or `""`
   * for Skip. Forwarded as `topic_focus`. (`""` is meaningful — it signals
   * "skip, answer unfocused" — so it is forwarded even though empty.)
   */
  topicFocus?: string;
  /**
   * The pre-reader clarification questions already shown to the user this
   * turn-chain (the Stage-1 enhancements). Forwarded as
   * `prior_clarification_questions` so the post-reader agent's de-dup is
   * deterministic.
   */
  priorClarificationQuestions?: string[];
}

export async function postV2Turn(
  workflowId: string,
  message: string,
  clientRequestId?: string,
  finalQuestion?: string,
  regime?: FarRegime,
  options?: PostV2TurnOptions
): Promise<Turn | ClarificationEnvelope | TopicClarificationEnvelope> {
  // `final_question` is sent ONLY when resolving a clarification. Its
  // PRESENCE tells the backend to skip the clarification gate and answer
  // directly; its ABSENCE means a fresh query → the gate runs. We must
  // never forward a stale `final_question` from a prior turn (see §4.1
  // state hygiene) or the next vague query would skip the gate. `regime`
  // is the user's chosen FAR Version, carried alongside `final_question` on
  // the clarification-resolve handoff (defaults to "rfo" backend-side when
  // omitted; see legacy-far-readers spec §4.1).
  //
  // `focus_token` (post-reader topic-focus gate) is sent ONLY when resolving
  // a topic-focus round-trip; its presence skips Stage-1 AND the readers and
  // runs the synth on the held outputs + `topic_focus`. Like
  // `final_question`, it must never leak into a fresh send (post-reader spec
  // §4.1 / §9 state hygiene).
  const body: {
    message: string;
    client_request_id?: string;
    final_question?: string;
    regime?: FarRegime;
    focus_token?: string;
    topic_focus?: string;
    prior_clarification_questions?: string[];
  } = { message };
  if (clientRequestId) body.client_request_id = clientRequestId;
  if (finalQuestion !== undefined) body.final_question = finalQuestion;
  if (regime !== undefined) body.regime = regime;
  if (options?.focusToken !== undefined) body.focus_token = options.focusToken;
  if (options?.topicFocus !== undefined) body.topic_focus = options.topicFocus;
  if (options?.priorClarificationQuestions !== undefined) {
    body.prior_clarification_questions = options.priorClarificationQuestions;
  }
  const resp = await fetchWithChatFeedback(wfUrl(workflowId, '/v2/turn'), {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(body),
    credentials: 'include',
  });
  await assertOk(resp);
  const raw = (await resp.json()) as
    | RawV2Turn
    | ClarificationEnvelope
    | TopicClarificationEnvelope;
  // Discriminate on `status`: an answer turn never carries it.
  const status = (raw as { status?: string })?.status;
  if (status === 'clarification_needed') {
    return raw as ClarificationEnvelope;
  }
  if (status === 'topic_clarification_needed') {
    return normalizeTopicClarificationEnvelope(
      raw as Partial<TopicClarificationEnvelope> & Record<string, unknown>
    );
  }
  if (isFastAnswerRecoveryEnvelope(raw)) {
    throw new FastAnswerRecoveryError(raw);
  }
  return normalizeV2Turn(raw as RawV2Turn);
}

/**
 * Normalize a raw `topic_clarification_needed` response body into the
 * frontend `TopicClarificationEnvelope` shape. Carries the optional
 * `clarification` field (the pre-reader clarified question, when this
 * topic-focus turn followed a pre-reader clarification) through explicitly
 * so the focus resubmit can use it as the cache-miss `final_question`
 * fallback AND so the "Clarified: …" callout stays visible continuously.
 * Missing / non-string `clarification` normalizes to `null`.
 */
export function normalizeTopicClarificationEnvelope(
  raw: Partial<TopicClarificationEnvelope> & Record<string, unknown>
): TopicClarificationEnvelope {
  return {
    status: 'topic_clarification_needed',
    question: typeof raw.question === 'string' ? raw.question : '',
    topics: Array.isArray(raw.topics) ? (raw.topics as string[]) : [],
    original_query:
      typeof raw.original_query === 'string' ? raw.original_query : '',
    focus_token: typeof raw.focus_token === 'string' ? raw.focus_token : '',
    regime: raw.regime,
    clarification:
      typeof raw.clarification === 'string' ? raw.clarification : null,
    client_request_id: raw.client_request_id,
    session_id: raw.session_id,
  };
}

export async function deleteThread(workflowId: string): Promise<void> {
  const resp = await fetchWithChatFeedback(wfUrl(workflowId, '/thread'), {
    method: 'DELETE',
    headers: authHeaders(),
    credentials: 'include',
  });
  await assertOk(resp);
}

export async function uploadAttachment(
  workflowId: string,
  file: File
): Promise<Attachment> {
  const form = new FormData();
  form.append('file', file);
  const resp = await fetchWithChatFeedback(wfUrl(workflowId, '/attachments'), {
    method: 'POST',
    // Do NOT set Content-Type — browser sets multipart boundary.
    headers: authHeaders(),
    body: form,
    credentials: 'include',
  });
  await assertOk(resp);
  const body = (await resp.json()) as Attachment;
  return {
    filename: body.filename,
    size: body.size,
    mime_type: body.mime_type,
  };
}

export async function listAttachments(workflowId: string): Promise<Attachment[]> {
  const resp = await fetchWithChatFeedback(wfUrl(workflowId, '/attachments'), {
    method: 'GET',
    headers: authHeaders(),
    credentials: 'include',
  });
  await assertOk(resp);
  const body = (await resp.json()) as AttachmentsEnvelope;
  return Array.isArray(body.attachments) ? body.attachments : [];
}

export async function deleteAttachment(
  workflowId: string,
  filename: string
): Promise<void> {
  const resp = await fetchWithChatFeedback(
    `${wfUrl(workflowId, '/attachments')}/${encodeURIComponent(filename)}`,
    { method: 'DELETE', headers: authHeaders(), credentials: 'include' }
  );
  await assertOk(resp);
}

/**
 * Canonical token regexes — kept in sync with the backend v2 validators
 * (`farchat/agents/far_chat_validators_v2.py`):
 *
 *  - `FAR_TOKEN_RE`   → `FAR <part>.<section>[-<sub>](paragraph…)`
 *  - `DFARS_TOKEN_RE` → `DFARS <3-digit-part>.<section>[-<sub>](paragraph…)`
 *  - `MEMO_TOKEN_RE`  → `DARS|RFO 2026-O0017` (DoD class deviation memo id)
 */
const FAR_TOKEN_RE = /^FAR\s+(\d+\.\d+(?:-\d+)?)((?:\([a-zA-Z0-9]+\))*)$/;
const DFARS_TOKEN_RE = /^DFARS\s+(\d{3}\.\d+(?:-\d+)?)((?:\([a-zA-Z0-9]+\))*)$/;
const PGI_TOKEN_RE = /^PGI\s+(\d{3}\.\d+(?:-\d+)?)((?:\([a-zA-Z0-9]+\))*)$/;
const MEMO_TOKEN_RE = /^(DARS|RFO)[\s-](\d{4}-O?\d+)$/;

/**
 * Split a compound citation string into atomic tokens. Mirrors the
 * backend's `_split_compound_citation` semantics so a synthesizer
 * citation like `"FAR 6.104-1 / DFARS 206.104-70"` or
 * `"DFARS 206.104-70(a), DARS 2026-O0017"` becomes one Citation per atom.
 */
export function splitCompoundCitation(citation: string): string[] {
  if (typeof citation !== 'string') return [];
  const parts = citation
    .split(/\s*[/,]\s*/)
    .map((p) => p.trim())
    .filter((p) => p.length > 0);
  return parts.length > 0 ? parts : [citation.trim()];
}

/**
 * Build a Citation object from a single canonical token string. Branches
 * on token kind:
 *   - FAR   → acquisition.gov FAR overhaul anchor URL
 *   - DFARS → acquisition.gov DFARS deviation guide page (no per-section
 *             anchor exists yet; the deviation guide is the closest stable
 *             landing the user can be sent to)
 *   - DARS  → acquisition.gov DFARS deviation guide page (the index that
 *             hosts the underlying DoD memo PDFs)
 *   - unknown → no URL; hover still shows the raw token text
 */
function buildCitationFromToken(token: string, id: number): Citation {
  const trimmed = token.trim();

  const farMatch = trimmed.match(FAR_TOKEN_RE);
  if (farMatch) {
    const section = farMatch[1];
    const paragraph = farMatch[2] || undefined;
    const anchor = `FAR_${section.replace(/[.-]/g, '_')}`;
    return {
      id,
      source: 'rfo',
      kind: 'far',
      canonical: trimmed,
      section,
      paragraph,
      url: `https://acquisition.gov/far/part-${section.split('.')[0]}#${anchor}`,
      quote: '',
      verified: false,
    };
  }

  const dfarsMatch = trimmed.match(DFARS_TOKEN_RE);
  if (dfarsMatch) {
    const section = dfarsMatch[1];
    const paragraph = dfarsMatch[2] || undefined;
    return {
      id,
      source: 'rfo',
      kind: 'dfars',
      canonical: trimmed,
      section,
      paragraph,
      // DFARS deviations live on the FAR-overhaul deviation-guide index
      // page (see `farchat/common/dfars_scraper.py::DEVIATION_INDEX_URL`).
      // No per-section deep-link anchor exists for DFARS yet, so the
      // index page is the closest stable landing.
      url: 'https://www.acquisition.gov/far-overhaul/far-part-deviation-guide',
      quote: '',
      verified: false,
    };
  }

  const pgiMatch = trimmed.match(PGI_TOKEN_RE);
  if (pgiMatch) {
    const section = pgiMatch[1];
    const paragraph = pgiMatch[2] || undefined;
    return {
      id,
      source: 'rfo',
      kind: 'pgi',
      canonical: trimmed,
      section,
      paragraph,
      // PGI deep-link URL needs the static slug table (kept on the
      // backend at data/rfo_anchors/pgi_part_slugs.json). The frontend
      // doesn't carry it — fall back to the deviation guide index for
      // bare-string normalization. Backend-built citations carry the
      // proper URL via CitationOut.url.
      url: 'https://www.acquisition.gov/dfarspgi',
      quote: '',
      verified: false,
    };
  }

  const memoMatch = trimmed.match(MEMO_TOKEN_RE);
  if (memoMatch) {
    return {
      id,
      source: 'rfo',
      kind: 'memo',
      canonical: trimmed,
      // Class deviation memos are PDFs hosted under the deviation guide.
      // The wire payload doesn't carry the per-memo PDF URL (backend
      // stores only `List[str]` of canonical tokens), so we point at the
      // index. When the backend serializer is upgraded to send a richer
      // shape this branch should populate `url` from that field instead.
      url: 'https://www.acquisition.gov/far-overhaul/far-part-deviation-guide',
      quote: '',
      verified: false,
    };
  }

  // Unknown token: keep the canonical text but no link. The renderer
  // will degrade gracefully (no underline, hover tooltip with header).
  return {
    id,
    source: 'rfo',
    kind: 'unknown',
    canonical: trimmed,
    quote: '',
    verified: false,
  };
}

export function normalizeCitations(input: Array<Citation | string>): Citation[] {
  const out: Citation[] = [];
  let nextId = 1;
  for (const c of input) {
    if (typeof c === 'string') {
      // Compound citations like `"FAR 6.104-1 / DFARS 206.104-70"` arrive
      // as a single backend string. Split into atoms so each gets its
      // own clickable marker — mirrors the backend's
      // `_split_compound_citation` semantics in
      // `farchat/agents/far_chat_validators_v2.py`.
      const atoms = splitCompoundCitation(c);
      for (const atom of atoms) {
        out.push(buildCitationFromToken(atom, nextId++));
      }
    } else {
      out.push(c);
      nextId = Math.max(nextId, (c.id ?? 0) + 1);
    }
  }
  return out;
}

export interface AnchorEnvelope {
  part: number;
  part_url: string;
  anchors: Array<{ section: string; anchor_id: string; title?: string }>;
  fetched_at: string;
}

export async function fetchAnchors(part: number = 6): Promise<AnchorEnvelope> {
  const resp = await fetchWithChatFeedback(`${API_BASE_URL}/api/far-chat/anchors?part=${part}`, {
    method: 'GET',
    headers: authHeaders(),
    credentials: 'include',
  });
  await assertOk(resp);
  return (await resp.json()) as AnchorEnvelope;
}

/** Client-side UUID v4 (no crypto.randomUUID fallback for older browsers). */
export function clientRequestId(): string {
  // Prefer native crypto.randomUUID when available.
  if (typeof crypto !== 'undefined' && typeof (crypto as any).randomUUID === 'function') {
    return (crypto as any).randomUUID();
  }
  // RFC4122 v4 (not cryptographically strong, but adequate for idempotency key).
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}
